import { useEffect, useRef } from 'react';
import { UserMessage } from './UserMessage';
import { AudioMessage } from './AudioMessage';
import Loader from './Loader';
import { useSmoothScroll } from '@/hooks/useSmoothScroll';
import type { Message } from './TextToSpeechApp';
import aawazAiImage from '@/assets/aawaz-ai-png.png';

interface ChatInterfaceProps {
  messages: Message[];
  isLoading: boolean;
  onUpdateMessage: (messageId: string, audioUrl: string, timestampsUrl?: string) => void;
}

export function AawazChatInterface({ messages, isLoading, onUpdateMessage }: ChatInterfaceProps) {
  // Use smooth scrolling hook for better performance
  const { messagesEndRef, setScrollContainer, scrollToBottom } = useSmoothScroll({
    enabled: true,
    scrollThreshold: 100,
    scrollDuration: 200,
  });

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  return (
    <div
      ref={setScrollContainer}
      className="flex-1 bg-background overflow-y-auto overflow-x-hidden min-w-0 px-2 xs:px-4 pb-32"
    >
      <div className="max-w-2xl mx-auto w-full space-y-4 pt-4">
        {messages.length === 0 && !isLoading && (
          <div className="flex-1 relative top-16 flex items-center justify-center">
            <div className="text-center space-y-4 max-w-md relative">
              <div className="relative w-48 h-48 mx-auto overflow-hidden bg-background-alt rounded-md">
                <img
                  src={aawazAiImage}
                  alt="Aawaz AI"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-foreground">
                  Give a Voice to your words
                </h2>
                <p className="text-muted-foreground">
                  Type your message below and experience natural voice synthesis with real-time word highlighting.
                </p>
              </div>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div key={message.id} className="space-y-2">
            <UserMessage text={message.text} />
            {message.audioUrl && (
              <AudioMessage
                message={message}
                onUpdateMessage={onUpdateMessage}
              />
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-transparent text-foreground px-4 py-2 rounded-3xl">
              <Loader />
            </div>
          </div>
        )}
      </div>

      <div ref={messagesEndRef} />
    </div>
  );
}